import React from 'react';
import { addDays, format, isEqual } from 'date-fns';

// Material-UI
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Chip from '@mui/material/Chip';
import { amber, blue, blueGrey, green, red } from '@mui/material/colors';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridOverlay,
  GridRowSelectiomModel
} from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PanToolIcon from '@mui/icons-material/PanTool';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  RequestNight
} from "../../interfaces";

import {
  DateRange,
  RequestGridHeader,
  RequestGridRow
} from '../../models';

import {
  EMPTY_STRING,
  SignupStatusID
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';
interface NightsGridProps {
  dateRange: DateRange;
  handleRequestsSelected: (requestsSelected: GridRowSelectiomModel) => void;
  handleRequestSummary: (signupID: number) => void;
  headers: Array<RequestGridHeader>;
  isLoading: boolean;
  nights: number;
  requestsSelected: GridRowSelectiomModel;
  rows: Array<RequestGridRow>;
}

const NightsGrid: React.FC<NightsGridProps> = (props) => {

  const { isSignupAdmin } = useAuth0User();

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      '& .super-app-theme--header': {
        backgroundColor: 'rgba(255, 7, 0, 0.55)'
      }
    },
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    cancelled: {
      color: blue[500]
    },
    cancelledlate: {
      color: red[500]
    },
    nightCount: {
      backgroundColor: '#FFFFFF',
      color: '#000000',
      margin: theme.spacing(0, 0, 0, 1)
    },
    noRows: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2, 0, 2, 0)
    },
    pending: {
      color: blueGrey[500]
    },
    waitlist: {
      color: amber[500]
    }
  };

  // #endregion

  // #region Methods

  /*
 * Grid column definitions
 */
  const columns = (): Array<GridColDef> => {

    const base: Array<GridColDef> = [
      {
        field: 'signupID',
        headerName: EMPTY_STRING,
        width: 50,
        type: 'string',
        renderCell: (params: GridCellParams) => (
          renderSummaryButton(params)
        )
      }, {
        field: 'referenceNumber',
        headerName: '#',
        width: 85,
        type: 'string',
      }, {
        field: 'fullName',
        headerName: 'Member/Guest',
        width: 175,
        type: 'string',
        renderCell: (params: GridCellParams) => (
          renderName(params)
        )
      }, {
        field: 'personType',
        headerName: 'Type',
        width: 150,
        type: 'string',
      }
    ];

    const nights: Array<GridColDef> = [];

    for (let i = 0; i < props.nights; i++) {
      nights.push(
        {
          field: `nights` + i.toString(),
          headerAlign: 'center',
          renderHeader: (params: GridColumnHeaderParams) => (
            renderHeader(params, i)
          ),
          align: 'center',
          width: 145,
          type: 'string',
          renderCell: (params: GridCellParams) => (
            renderStatus(params, i)
          )
        }
      );
    }

    return [...base, ...nights];
  }

  /*
   * Overlay displayed when no data
   */
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No lodge use request nights for the selected date range</div>
      </GridOverlay>
    );
  }

  /*
   * Render the header for a date column
   */
  const renderHeader = (params: GridColumnHeaderParams, index: number) => {

    if (props.dateRange.startDate !== EMPTY_STRING && props.dateRange.endDate !== EMPTY_STRING) {
      const start = new Date(props.dateRange.startDate);
      const date = format(addDays(start, index), 'EEEEEE M/d');

      return (
        <div><span>{date}</span>{renderNightCount(addDays(start, index))}</div>
      );
    }

  }

  /*
   * Render name for row
   */
  const renderName = (params: GridCellParams) => {
    const isG = params.row.isGuest as boolean;

    if (isG) {
      return (
        <Tooltip title={`Guest of ${params.row.guestOf || ''}`} arrow>
          <span>{params.row.rank}-{params.row.lastName || ''}, {params.row.firstName || ''}{params.row.priorityGuest ? '*' : EMPTY_STRING}</span>
        </Tooltip>
      );
    } else {
      return (
        <span>{params.row.rank}-{params.row.lastName || ''}, {params.row.firstName || ''}{isSignupAdmin() ? params.row.signedWaiver ? ' \u2611' : ' \u2666' : EMPTY_STRING}</span>
      );
    }

  }

  /*
 * Render night count and color based upon rank
 */
  const renderNightCount = (date: Date) => {

    const h = props.headers.find(rh => {
      return isEqual(new Date(rh.lodgeNight), date);
    });

    if (h !== undefined) {

      return (
        <Chip
          variant="outlined"
          size="small"
          sx={styles.nightCount}
          label={h.nightCount} />
      )
    } else {
      return (<span />)
    }

  }

  /*
   * Render name for row
   */
  const renderSummaryButton = (params: GridCellParams) => {

    //const id = params.row.signupID;

    return (
      <Tooltip title={`Request Summary`} arrow>
        <IconButton
          size="small"
          onClick={() => handleSummary(params.row.signupID)}
        >
          <ListAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );

  }

  /*
   * Render the request status icon
   */
  const renderStatus = (params: GridCellParams, index: number) => {

    const nights = params.row.nights as Array<RequestNight>;
    const start = new Date(props.dateRange.startDate);
    const date = addDays(start, index);

    const night = nights.find(rn => {
      return isEqual(new Date(rn.lodgeNight), date);
    });

    if (night !== undefined) {
      switch (night.statusID) {
        case SignupStatusID.Requested:
          return (
            <Tooltip title={night.status} arrow>
              <PendingIcon fontSize="medium" sx={styles.pending} />
            </Tooltip>
          )
        case SignupStatusID.Confirmed:
          return (
            <Tooltip title={night.status} arrow>
              <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
            </Tooltip>
          )
        case SignupStatusID.Waitlist:
          return (
            <Tooltip title={night.status} arrow>
              <PanToolIcon fontSize="medium" sx={styles.waitlist} />
            </Tooltip>
          )
        case SignupStatusID.Denied:
          return (
            <Tooltip title={night.status} arrow>
              <BlockIcon fontSize="medium" sx={styles.deined} />
            </Tooltip>
          )
        case SignupStatusID.Cancelled:
          return (
            <Tooltip title={night.status} arrow>
              <CancelIcon fontSize="medium" sx={styles.cancelled} />
            </Tooltip>
          )
        case SignupStatusID.CancelledLate:
          return (
            <Tooltip title={night.status} arrow>
              <CancelIcon fontSize="medium" sx={styles.cancelledlate} />
            </Tooltip>
          )
        default:
          return (<span />)
      }
    }

  }

  // #endregion

  // #region Event handlers

  /*
  * Handle request summary button click
  */
  const handleSummary = (id: number) => {
    props.handleRequestSummary(id);
  }

  // #endregion

  return (
    <DataGrid
      checkboxSelection={isSignupAdmin()}
      columns={columns()}
      slots={{
        noRowsOverlay: noRowsOverlay
      }}
      density="compact"
      disableColumnSelector
      disableRowSelectionExcludeModel
      disableRowSelectionOnClick
      getRowId={(row) => row.rowID} // Special unique ID for each row
      hideFooter
      loading={props.isLoading}
      onRowSelectionModelChange={(r: GridRowSelectiomModel) => props.handleRequestsSelected(r)}
      rows={props.rows}
      rowSelectionModel={props.requestsSelected}
      sx={styles.root}
    />
  );

}

export default NightsGrid;